/**
 * XLSX import flow — the original secondary path for bulk patch.
 *
 * Downloads a template, parses an uploaded XLSX, posts dry-run → apply
 * to the same backend endpoint the UI-driven flow uses. Kept available
 * for advanced/batch use cases where editing 200+ rows in a browser
 * table would be slower than editing the spreadsheet locally.
 */

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { baseUrl } from 'api/config'
import {
  BulkPatchResult,
  BulkPatchRowResult,
  useBulkPatch,
} from 'api/aws/deals/useBulkPatch'
import React, { useMemo, useRef, useState } from 'react'

import { parseTemplateXlsx, ParseResult } from './parseTemplateXlsx'

type StatusFilter = 'all' | 'would_apply' | 'applied' | 'blocked' | 'error'

const XlsxImportFlow: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)
  const [parsed, setParsed] = useState<ParseResult | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [previewResult, setPreviewResult] = useState<BulkPatchResult | null>(null)
  const [applyResult, setApplyResult] = useState<BulkPatchResult | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  const { mutateAsync: runBulkPatch, isPending: isMutating } = useBulkPatch()
  const isRunningPreview = isMutating && !applyResult && !previewResult
  const isApplying = isMutating && previewResult != null && !applyResult

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadedFileName(file.name)
    setParseError(null)
    setParsed(null)
    setPreviewResult(null)
    setApplyResult(null)
    try {
      const result = await parseTemplateXlsx(file)
      setParsed(result)
    } catch (err) {
      setParseError(err instanceof Error ? err.message : String(err))
    }
  }

  const handleRunPreview = async (): Promise<void> => {
    if (!parsed) return
    try {
      const result = await runBulkPatch({ rows: parsed.rows, dryRun: true })
      setPreviewResult(result)
      setStatusFilter('all')
    } catch (err) {
      setParseError(err instanceof Error ? err.message : String(err))
    }
  }

  const handleApply = async (): Promise<void> => {
    if (!parsed) return
    try {
      const result = await runBulkPatch({ rows: parsed.rows, dryRun: false })
      setApplyResult(result)
      setStatusFilter('all')
    } catch (err) {
      setParseError(err instanceof Error ? err.message : String(err))
    }
  }

  const handleReset = (): void => {
    setUploadedFileName(null)
    setParsed(null)
    setParseError(null)
    setPreviewResult(null)
    setApplyResult(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const displayResult = applyResult ?? previewResult
  const filteredRows = useMemo<BulkPatchRowResult[]>(() => {
    if (!displayResult) return []
    if (statusFilter === 'all') return displayResult.results
    return displayResult.results.filter(r => r.status === statusFilter)
  }, [displayResult, statusFilter])

  return (
    <Stack spacing={4}>
      {(parsed || previewResult || applyResult) && (
        <Stack direction="row" justifyContent="flex-end">
          <Button onClick={handleReset} variant="outlined" size="small">
            Start over
          </Button>
        </Stack>
      )}

      {/* ── Step 1: Download + Upload ──────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 }, borderRadius: 2, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600 }}>
          1. Download the template, fill it out, and upload
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            href={`${baseUrl}/aws/deals/bulk-patch/template`}
            download="bulk-patch-template.xlsx"
          >
            Download template
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            component="label"
            sx={{ ml: 1 }}
          >
            Upload filled template
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileChange}
            />
          </Button>
          {uploadedFileName && (
            <Typography variant="body2" color="text.secondary">
              Uploaded: <strong>{uploadedFileName}</strong>
            </Typography>
          )}
        </Stack>
        {parseError && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setParseError(null)}>
            {parseError}
          </Alert>
        )}
      </Paper>

      {/* ── Step 2: Parsed-rows summary ────────────────────────────── */}
      {parsed && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            2. File parsed
          </Typography>
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {parsed.rows.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                rows ready to preview
              </Typography>
            </Box>
            {parsed.empty_rows_dropped > 0 && (
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {parsed.empty_rows_dropped}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  blank rows in template (skipped)
                </Typography>
              </Box>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleRunPreview}
                disabled={isRunningPreview || parsed.rows.length === 0}
              >
                {isRunningPreview ? (
                  <>
                    <CircularProgress size={18} sx={{ mr: 1 }} />
                    Running preview…
                  </>
                ) : (
                  'Run preview'
                )}
              </Button>
            </Box>
          </Stack>
          {parsed.warnings.map((w, i) => (
            <Alert key={i} severity="warning" sx={{ mt: 2 }} icon={<WarningIcon />}>
              {w}
            </Alert>
          ))}
        </Paper>
      )}

      {/* ── Step 3 / 4: Preview + Apply Results ────────────────────── */}
      {displayResult && (
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">
              {applyResult ? '4. Apply results' : '3. Dry-run preview'}
            </Typography>
            {applyResult ? (
              <Chip
                icon={<CheckCircleIcon />}
                label={`Applied · ${applyResult.applied} row(s) committed`}
                color="success"
              />
            ) : (
              <Chip label="No writes yet — review below" variant="outlined" />
            )}
          </Stack>

          <Stack direction="row" spacing={3} sx={{ mb: 3 }} flexWrap="wrap">
            <CountCard
              label={applyResult ? 'Applied' : 'Would apply'}
              value={applyResult ? applyResult.applied : displayResult.would_apply}
              color="success"
            />
            <CountCard label="Blocked" value={displayResult.blocked} color="warning" />
            <CountCard label="Errored" value={displayResult.errored} color="error" />
            <CountCard label="Total rows" value={displayResult.total_rows} color="default" />
          </Stack>

          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Filter:
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={statusFilter}
              onChange={(_, v: StatusFilter | null) => v && setStatusFilter(v)}
            >
              <ToggleButton value="all">All ({displayResult.results.length})</ToggleButton>
              {applyResult ? (
                <ToggleButton value="applied">Applied ({applyResult.applied})</ToggleButton>
              ) : (
                <ToggleButton value="would_apply">
                  Would apply ({displayResult.would_apply})
                </ToggleButton>
              )}
              <ToggleButton value="blocked">Blocked ({displayResult.blocked})</ToggleButton>
              {displayResult.errored > 0 && (
                <ToggleButton value="error">Errored ({displayResult.errored})</ToggleButton>
              )}
            </ToggleButtonGroup>
          </Stack>

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480, mb: 3 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 60 }}>Row</TableCell>
                  <TableCell>Org</TableCell>
                  <TableCell>Deal</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((r, i) => (
                  <RowResultRow key={`${r.row_index ?? i}-${i}`} row={r} />
                ))}
                {filteredRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No rows match this filter.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {previewResult && !applyResult && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  Confirm to commit <strong>{previewResult.would_apply}</strong> row(s) to MOTHERSHIP
                  and fire {previewResult.would_apply} Heartbeat push(es). Sequential — ~
                  {Math.max(1, Math.ceil(previewResult.would_apply * 1.5))}s estimated.
                </Typography>
                <Button
                  variant="contained"
                  color="warning"
                  size="large"
                  onClick={handleApply}
                  disabled={isApplying || previewResult.would_apply === 0}
                >
                  {isApplying ? (
                    <>
                      <CircularProgress size={18} sx={{ mr: 1 }} />
                      Applying…
                    </>
                  ) : (
                    `Apply ${previewResult.would_apply} change(s)`
                  )}
                </Button>
              </Stack>
            </>
          )}
        </Paper>
      )}
    </Stack>
  )
}

const CountCard: React.FC<{
  label: string
  value: number
  color: 'success' | 'warning' | 'error' | 'default'
}> = ({ label, value, color }) => {
  const palette: Record<typeof color, string> = {
    success: 'success.main',
    warning: 'warning.main',
    error: 'error.main',
    default: 'text.primary',
  }
  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, color: palette[color] }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  )
}

const RowResultRow: React.FC<{ row: BulkPatchRowResult }> = ({ row }) => {
  return (
    <TableRow hover>
      <TableCell>{row.row_index ?? '—'}</TableCell>
      <TableCell>
        <Typography variant="body2">{row.org_name ?? '(unknown)'}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {row.grobotic_org_pk}
        </Typography>
      </TableCell>
      <TableCell>
        {row.resolved_deal_name ? (
          <>
            <Typography variant="body2">{row.resolved_deal_name}</Typography>
            {row.new_semantic_version && (
              <Typography variant="caption" color="success.main">
                → v{row.new_semantic_version}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="caption" color="text.secondary">
            (not resolved)
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <StatusChip row={row} />
      </TableCell>
      <TableCell>
        {row.status === 'blocked' || row.status === 'error' ? (
          <Box>
            <Typography variant="caption" color="error.main" sx={{ fontFamily: 'monospace' }}>
              {row.reason}
            </Typography>
            {row.message && (
              <Typography variant="caption" display="block" color="text.secondary">
                {row.message}
              </Typography>
            )}
          </Box>
        ) : (
          <DiffSummary diff={row.diff} />
        )}
        {row.heartbeat_push && (
          <Typography
            variant="caption"
            display="block"
            color={row.heartbeat_push.success ? 'success.main' : 'warning.main'}
            sx={{ mt: 0.5 }}
          >
            Heartbeat: {row.heartbeat_push.success ? '✓' : '⚠'}{' '}
            {row.heartbeat_push.flow ? `flow=${row.heartbeat_push.flow}` : ''}{' '}
            {row.heartbeat_push.emitted_submissions != null
              ? `· ${row.heartbeat_push.emitted_submissions} submission(s)`
              : ''}
            {row.heartbeat_push.skip_reason ? ` · skip=${row.heartbeat_push.skip_reason}` : ''}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  )
}

const StatusChip: React.FC<{ row: BulkPatchRowResult }> = ({ row }) => {
  switch (row.status) {
    case 'applied':
      return <Chip icon={<CheckCircleIcon />} label="Applied" color="success" size="small" />
    case 'would_apply':
      return <Chip label="Would apply" color="success" variant="outlined" size="small" />
    case 'blocked':
      return <Chip icon={<WarningIcon />} label="Blocked" color="warning" size="small" />
    case 'error':
      return <Chip icon={<ErrorIcon />} label="Error" color="error" size="small" />
  }
}

const DiffSummary: React.FC<{ diff: BulkPatchRowResult['diff'] }> = ({ diff }) => {
  if (!diff || diff.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        (no changes)
      </Typography>
    )
  }
  return (
    <Stack spacing={0.3}>
      {diff.map((d, i) => (
        <Typography
          key={i}
          variant="caption"
          sx={{ fontFamily: 'monospace', fontSize: 11 }}
        >
          <strong>{d.product_name}</strong> · {d.field}:{' '}
          <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>{formatValue(d.old_value)}</span>{' '}
          → <span style={{ color: '#1b5e20' }}>{formatValue(d.new_value)}</span>
        </Typography>
      ))}
    </Stack>
  )
}

function formatValue(v: number | string | null): string {
  if (v == null || v === '') return '—'
  return String(v)
}

export default XlsxImportFlow
